import React, { useCallback, useEffect, useMemo, useState } from "react";

import type TTranscription from "../types/TTranscription";

// 1. Import the Autonomous Brain
import { useKernel } from "../hooks/AIKernel"; // Adjust relative path based on your folder structure

export type TAppContext = {
  timelineStartTime: number;
  setTimelineStartTime: (value: number) => void;
  currentSeekTime: number;
  setCurrentSeekTime: (value: number) => void;
  timelineEndTime: number;
  setTimelineEndTime: (value: number) => void;
  videoRef: HTMLVideoElement | null;
  setVideoRef: (instance: HTMLVideoElement | null) => void;
  isProcessing: boolean;
  setIsProcessing: (isProcessing: boolean) => void;
  resetEditState: () => void;
  mobileSidebarToggle: (status?: boolean) => void;
  setIsSidebarClosing: (value: boolean) => void;
  isMobileSidebarOpen: boolean;
  subtitles: TTranscription[] | null;
  setSubtitles: (subtitles: TTranscription[] | null) => void;
  setIsLogoEditorOpen: (value: boolean) => void;
  isLogoEditorOpen: boolean;
  logoEditorToggle: (status?: boolean) => void;
};

export const AppContext = React.createContext<TAppContext | null>(null);

export default function AppContextProvider({
  children,
}: React.PropsWithChildren) {
  const [timelineStartTime, setTimelineStartTime] = useState(0);
  const [currentSeekTime, setCurrentSeekTime] = useState(0);
  const [timelineEndTime, setTimelineEndTime] = useState(0);
  const [videoRef, _setVideoRef] = useState<TAppContext["videoRef"]>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSidebarClosing, setIsSidebarClosing] = useState(false);

  const [isLogoEditorOpen, setIsLogoEditorOpen] = useState(false);

  const [subtitles, setSubtitles] = useState<TAppContext["subtitles"]>(null);

  // 2. Connect the Global Context to the Agent's Memory Matrix
  const { reportBehavior } = useKernel();

  const resetEditState = useCallback(() => {
    setCurrentSeekTime(0);
    setTimelineStartTime(0);
    videoRef?.duration && setTimelineEndTime(videoRef?.duration);
  }, [videoRef]);

  const mobileSidebarToggle = useCallback<TAppContext["mobileSidebarToggle"]>(
    (status) => {
      let nextState = false;

      if (status !== undefined) {
        nextState = status;
        setIsMobileSidebarOpen(status);
      } else if (!isSidebarClosing) {
        setIsMobileSidebarOpen((open) => {
          nextState = !open;
          return nextState;
        });
      }

      // TELEMETRY: Capture advanced tool intent via the sidebar
      if (nextState && reportBehavior) {
        reportBehavior("mobile_tools_intent", 2, "interest");
      }
    },
    [isSidebarClosing, reportBehavior]
  );

  const logoEditorToggle = useCallback<TAppContext["logoEditorToggle"]>(
    (status) => {
      let nextState = false;

      if (status !== undefined) {
        nextState = status;
        setIsLogoEditorOpen(status);
      } else if (!isSidebarClosing) {
        setIsLogoEditorOpen((open) => {
          nextState = !open;
          return nextState;
        });
      }

      // TELEMETRY: Capture explicit branding intent
      if (nextState && reportBehavior) {
        reportBehavior("branding_intent", 3, "interest");
      }
    },
    [isSidebarClosing, reportBehavior]
  );

  const observer = useMemo(
    () =>
      new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "src"
          ) {
            resetEditState();
            
            // TELEMETRY: A DOM-level confirmation that the video source mutated (e.g., render success or new upload)
            if (reportBehavior) {
              reportBehavior("video_pipeline_updated", 2, "behavior");
            }
          }
        }
      }),
    [resetEditState, reportBehavior]
  );

  useEffect(() => {
    if (videoRef) {
      videoRef.addEventListener("loadedmetadata", () => {
        const duration = ~~Math.floor(videoRef?.duration);
        setTimelineEndTime(duration);
      });

      observer.observe(videoRef, { attributes: true });
    }

    return () => observer.disconnect();
  }, [observer, videoRef]);

  const setVideoRef = useCallback<TAppContext["setVideoRef"]>((ref) => {
    if (ref) {
      _setVideoRef(ref);
    }
  }, []);

  return (
    <AppContext.Provider
      value={{
        timelineStartTime,
        setTimelineStartTime,
        timelineEndTime,
        setTimelineEndTime,
        videoRef,
        setVideoRef,
        isProcessing,
        setIsProcessing,
        currentSeekTime,
        setCurrentSeekTime,
        resetEditState,
        mobileSidebarToggle,
        setIsSidebarClosing,
        isMobileSidebarOpen,
        subtitles,
        setSubtitles,
        isLogoEditorOpen,
        setIsLogoEditorOpen,
        logoEditorToggle,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}